/**
 * Overleg verzetten of overslaan (Joris 20 okt 2026: MT-punt van maandag ging niet door en werd dinsdag gehouden,
 * maar Mijn week toonde alleen komende overleggen). Gedeeld door Mijn week, het blok op Vandaag en de overlegpagina.
 *
 * Vangnet: verzetten en overslaan krijgen een ongedaan-toast die de vorige datum/tijd of status terugzet.
 * Rechten bewaakt de engine (voorzitter/eigenaar + schrijfrecht); `mag_verzetten` bepaalt alleen of je de knop ziet.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { overlegApi } from './overlegApi';
import { datumKort } from './OverlegDelen';

/** Vandaag als JJJJ-MM-DD in NL-tijd (de engine rekent ook in Europe/Amsterdam). */
export const vandaagNl = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
/** UU:MM uit een ISO-moment met NL-offset ("2026-10-19T09:00:00+02:00"), zonder browser-tijdzone. */
export const tijdUitNl = (iso) => (typeof iso === 'string' && iso.length >= 16 ? iso.slice(11, 16) : '');

/** De drie handelingen, elk met ongedaan maken. `naVerandering` herlaadt de lijst. */
export function useVerzetten(naVerandering) {
  const navigate = useNavigate();
  const herlaad = async () => { try { await naVerandering?.(); } catch { /* lijst verversen mag stil falen */ } };

  const verzet = async (s, datum, tijd, { openen = false } = {}) => {
    const terug = { datum: s.datum, tijd: tijdUitNl(s.gepland_start_nl) };
    try {
      await overlegApi.live(s.id, 'verzetten', { datum, ...(tijd ? { tijd } : {}) });
    } catch (e) { toast.error(e.message || 'Verzetten mislukt'); return false; }
    biedOngedaanAan(`${s.type_naam} verzet naar ${datumKort(datum)}${tijd ? ` ${tijd}` : ''}`, async () => {
      await overlegApi.live(s.id, 'verzetten', terug);
      await herlaad();
    }, { eigenKnop: true });
    if (openen) navigate(`/overleg/sessie/${s.id}`);
    else await herlaad();
    return true;
  };

  const overslaan = async (s) => {
    const vorige = s.status;
    try { await overlegApi.live(s.id, 'overslaan'); } catch (e) { toast.error(e.message || 'Overslaan mislukt'); return false; }
    biedOngedaanAan(`${s.type_naam} van ${datumKort(s.datum)} overgeslagen`, async () => {
      await overlegApi.live(s.id, 'terugzetten', { status: vorige });
      await herlaad();
    }, { eigenKnop: true });
    await herlaad();
    return true;
  };

  return { verzet, vandaagHouden: (s) => verzet(s, vandaagNl(), '', { openen: true }), overslaan };
}

/** Datum- en tijdkiezer. Zonder andere tijd geldt de vaste tijd van het overleg. */
export function VerzetDialoog({ sessie, onClose, onVerzet }) {
  const [datum, setDatum] = useState(() => (sessie && sessie.datum >= vandaagNl() ? sessie.datum : vandaagNl()));
  const [tijd, setTijd] = useState(() => tijdUitNl(sessie?.gepland_start_nl));
  const [bezig, setBezig] = useState(false);
  if (!sessie) return null;
  const opslaan = async () => {
    setBezig(true);
    const ok = await onVerzet(sessie, datum, tijd);
    setBezig(false);
    if (ok) onClose();
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>{sessie.type_naam} verzetten</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>Gepland op {datumKort(sessie.datum)} {tijdUitNl(sessie.gepland_start_nl)}. Voorbereiding en acties gaan mee.</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField label="Datum" type="date" size="small" value={datum} onChange={(e) => setDatum(e.target.value)}
            inputProps={{ min: vandaagNl() }} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          <TextField label="Tijd" type="time" size="small" value={tijd} onChange={(e) => setTijd(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={!datum || bezig} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 700 }}>Verzetten</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Kleine "…"-knop bij een komend overleg; alleen voor wie mag verzetten. Klik lekt niet door naar de kaart. */
export function VerzetMenuKnop({ sessie, onVerzet, sx }) {
  const [anker, setAnker] = useState(null);
  const [dialoog, setDialoog] = useState(false);
  if (!sessie?.mag_verzetten) return null;
  return (
    <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', ...sx }}>
      <Tooltip title="Meer"><IconButton size="small" aria-label={`Meer bij ${sessie.type_naam}`} onClick={(e) => setAnker(e.currentTarget)} sx={{ p: 0.25, color: '#94a3b8' }}><MoreHorizIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      <Menu anchorEl={anker} open={!!anker} onClose={() => setAnker(null)}>
        <MenuItem onClick={() => { setAnker(null); setDialoog(true); }} sx={{ fontSize: 13.5 }}>Verzetten…</MenuItem>
      </Menu>
      {dialoog && <VerzetDialoog sessie={sessie} onClose={() => setDialoog(false)} onVerzet={onVerzet} />}
    </Box>
  );
}

/**
 * "Niet gehouden": overleggen van de afgelopen twee weken die nooit zijn gestart. Rustig: één regel per overleg,
 * de knoppen alleen voor wie mag verzetten. `compact` (Vandaag): alleen Vandaag houden, de rest achter "…".
 */
export function NietGehouden({ items, onVeranderd, compact = false }) {
  const { verzet, vandaagHouden, overslaan } = useVerzetten(onVeranderd);
  const [anker, setAnker] = useState(null);       // { el, sessie }
  const [dialoog, setDialoog] = useState(null);
  if (!items?.length) return null;
  const knop = { textTransform: 'none', fontWeight: 700, minWidth: 0, whiteSpace: 'nowrap', fontSize: compact ? 12.5 : 13 };
  return (
    <Box sx={compact ? { mb: 0.5 } : { mb: 2, pb: 1.5, borderBottom: '1px solid #eef2f6' }}>
      {!compact && <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 0.5 }}>Niet gehouden</Typography>}
      <Stack spacing={0.5}>
        {items.map((s) => (
          <Stack key={s.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography sx={{ flex: 1, minWidth: 160, fontSize: compact ? 12.5 : 13.5, color: '#475569' }}>
              <b>{s.type_naam}</b> van {datumKort(s.datum)} ging niet door
            </Typography>
            {/* Geen fragment als kind van een Stack met spacing (wit scherm, zie memory): elke knop apart. */}
            {s.mag_verzetten && <Button size="small" onClick={() => vandaagHouden(s)} sx={knop}>Vandaag houden</Button>}
            {s.mag_verzetten && !compact && <Button size="small" color="inherit" onClick={() => setDialoog(s)} sx={{ ...knop, color: '#526075' }}>Verzetten…</Button>}
            {s.mag_verzetten && !compact && <Button size="small" color="inherit" onClick={() => overslaan(s)} sx={{ ...knop, color: '#94a3b8' }}>Overslaan</Button>}
            {s.mag_verzetten && compact && (
              <IconButton size="small" aria-label={`Meer bij ${s.type_naam}`} onClick={(e) => setAnker({ el: e.currentTarget, sessie: s })} sx={{ p: 0.25, color: '#94a3b8' }}>
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>
      <Menu anchorEl={anker?.el} open={!!anker} onClose={() => setAnker(null)}>
        <MenuItem onClick={() => { const s = anker.sessie; setAnker(null); setDialoog(s); }} sx={{ fontSize: 13.5 }}>Verzetten…</MenuItem>
        <MenuItem onClick={() => { const s = anker.sessie; setAnker(null); overslaan(s); }} sx={{ fontSize: 13.5 }}>Overslaan</MenuItem>
      </Menu>
      {dialoog && <VerzetDialoog sessie={dialoog} onClose={() => setDialoog(null)} onVerzet={verzet} />}
    </Box>
  );
}
